// Parses Mintlify's `<Color>` MDX into our color node HTML. No markdown
// parser here, so the v2 reader and the importer can each bring their own.

const ATTRIBUTE_VALUE = String.raw`(?:"[^"]*"|'[^']*'|\{\{[\s\S]*?\}\}|\{[^}]*\})`
const ATTRIBUTES = String.raw`((?:\s+[\w-]+(?:\s*=\s*${ATTRIBUTE_VALUE})?)*)`

export const COLOR_BLOCK = new RegExp(
	String.raw`<Color${ATTRIBUTES}\s*>([\s\S]*?)<\/Color>`,
	"g",
)
const ROW_BLOCK = new RegExp(
	String.raw`<Color\.Row${ATTRIBUTES}\s*>([\s\S]*?)<\/Color\.Row>`,
	"g",
)
const ITEM_TAG = new RegExp(String.raw`<Color\.Item${ATTRIBUTES}\s*\/?>`, "g")

const decode = (value: string): string =>
	value
		.replace(/&quot;/g, '"')
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&amp;/g, "&")

const escapeAttribute = (value: string): string =>
	value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;")

function readAttribute(attributes: string, name: string): string | null {
	const match = new RegExp(
		String.raw`(?:^|\s)${name}\s*=\s*(?:"([^"]*)"|'([^']*)'|(\{\{[\s\S]*?\}\})|\{([^}]*)\})`,
	).exec(attributes)
	if (!match) return null
	if (match[1] !== undefined) return decode(match[1])
	if (match[2] !== undefined) return decode(match[2])
	if (match[3] !== undefined) return match[3]
	const expression = (match[4] ?? "").trim()
	return /^["'`]([\s\S]*)["'`]$/.exec(expression)?.[1] ?? expression
}

export interface ColorItemValue {
	name: string
	value: string
	dark: string | null
}

export function readColorItem(attributes: string): ColorItemValue {
	const name = readAttribute(attributes, "name") ?? ""
	const raw = readAttribute(attributes, "value") ?? ""
	if (raw.startsWith("{{")) {
		const light = /light\s*:\s*["']([^"']*)["']/.exec(raw)?.[1] ?? ""
		const dark = /dark\s*:\s*["']([^"']*)["']/.exec(raw)?.[1] ?? null
		return {
			name,
			value: light || dark || "",
			dark: dark && dark !== light ? dark : null,
		}
	}
	return { name, value: raw, dark: null }
}

export function colorItemHtml(item: ColorItemValue): string {
	return `<div data-type="color-item" data-name="${escapeAttribute(item.name)}" data-value="${escapeAttribute(
		item.value,
	)}"${item.dark ? ` data-dark="${escapeAttribute(item.dark)}"` : ""}></div>`
}

const itemsIn = (source: string): string =>
	[...source.matchAll(ITEM_TAG)]
		.map((match) => colorItemHtml(readColorItem(match[1] ?? "")))
		.join("")

// `renderInline` turns a row title's markdown into inline HTML.
export function colorBlockHtml(
	body: string,
	renderInline: (markdown: string) => string,
): string | null {
	const rows = [...body.matchAll(ROW_BLOCK)].map((match) => {
		const title = readAttribute(match[1] ?? "", "title") ?? ""
		const items = itemsIn(match[2] ?? "")
		return items
			? `<div data-type="color-row"><p data-color-row-title>${
					title.trim() ? renderInline(title).replace(/\n/g, " ") : ""
				}</p>${items}</div>`
			: ""
	})
	const rowHtml = rows.join("")
	if (rowHtml) {
		return `<div data-type="color" data-variant="table">${rowHtml}</div>`
	}
	const items = itemsIn(body)
	if (!items) return null
	// Loose swatches always sit in the compact grid; a table needs rows.
	return `<div data-type="color" data-variant="compact">${items}</div>`
}
